import { useEffect, useState } from 'react';

import { fetchPhqGadQuestions } from '@/modules/api';
import type { SurveyQuestion } from '@/modules/survey';

// Sparta's split: phq/gad-1 & -2 are the abbreviated screen; -3 and up are the
// extended set shown only when the preliminary scores are elevated.
const ABBREVIATED = /(phq|gad)-(1|2)/i;
const EXTENDED = /(phq|gad)-([3-9]|\d{2,})/i;

type UsePhqGadQuestions = {
  abbreviated: SurveyQuestion[];
  extended: SurveyQuestion[];
  isLoading: boolean;
  error: unknown;
};

export function usePhqGadQuestions(): UsePhqGadQuestions {
  const [abbreviated, setAbbreviated] = useState<SurveyQuestion[]>([]);
  const [extended, setExtended] = useState<SurveyQuestion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    let cancelled = false;
    fetchPhqGadQuestions()
      .then((items) => {
        if (cancelled) return;
        // Responses submit by choice index, so the choice value is its `order`
        // (omit surveyChoiceUrn) — SurveyContent then yields the index.
        const mapped: SurveyQuestion[] = items
          .map((question) => ({
            key: question.key,
            order: question.order,
            text: question.text,
            additionalInfo: question.additionalInfo,
            choices: question.choices.map((choice) => ({ text: choice.text, order: choice.order })),
          }))
          .sort((a, b) => a.order - b.order);
        setAbbreviated(mapped.filter((question) => ABBREVIATED.test(question.key)));
        setExtended(mapped.filter((question) => EXTENDED.test(question.key)));
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err);
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return { abbreviated, extended, isLoading, error };
}
